import { formatCashClosingTime } from "./formatCashClosingTime";
import { jsPDF } from "jspdf";

// Función para formatear moneda
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Función para formatear número
const formatNumber = (value) => {
  if (!value && value !== 0) return "0";
  return value.toLocaleString("es-CO");
};

// Función para formatear fecha
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return (
      date.toLocaleDateString("es-CO") +
      " " +
      date.toLocaleTimeString("es-CO", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
    );
  } catch {
    return dateString;
  }
};

// Función para formatear fecha simple
const formatSimpleDate = (dateString) => {
  if (!dateString) return "N/A";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

// Función para limpiar el nombre del comercio
const cleanComercioName = (comercio) => {
  if (!comercio) return "";
  return comercio.split("/")[0];
};

// Función para obtener el valor de caja a mostrar
const getCajaDisplayValue = (boxValue, reportData) => {
  // Si se seleccionó una caja específica y no es "TODAS"
  if (boxValue !== "TODAS" && boxValue !== undefined && boxValue !== null && boxValue !== "") {
    // Convertir a string si es número
    return String(boxValue);
  }
  // Si no se seleccionó caja específica, mostrar la última caja que hizo cierre
  const boxLast = reportData?.raw?.login?.boxLast;
  if (boxLast !== undefined && boxLast !== null) {
    return `Caja ${boxLast} (Último cierre)`;
  }
  return "Todas";
};

// Función para dibujar línea horizontal
const drawLine = (doc, y) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setDrawColor(200, 200, 200);
  doc.line(15, y, pageWidth - 15, y);
};

// Función para agregar pie de página
const addFooter = (doc, empresaData, pageNumber, totalPages) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  doc.text(
    `${empresaData.version || "LINCE V7.0.R3"} | ${
      empresaData.razonSocial || "Documento oficial"
    } | Página ${pageNumber} de ${totalPages}`,
    pageWidth / 2,
    pageHeight - 10,
    { align: "center" }
  );
  doc.text(`Generado: ${formatDateTime(new Date().toISOString())}`, pageWidth / 2, pageHeight - 6, {
    align: "center",
  });
};

// Función para verificar si una sección completa cabe en la página
const sectionFits = (doc, startY, sectionHeight) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  return startY + sectionHeight + 15 <= pageHeight - 20;
};

// Función para agregar una sección completa (título + tabla de 2 columnas)
const addTwoColumnSection = (doc, title, data, startY, options = {}) => {
  let y = startY;
  const rowHeight = 6.5;
  const fontSize = 8.5;
  const leftColWidth = options.leftColWidth || 50;
  const rightColWidth = options.rightColWidth || 120;

  const sectionHeight = 15 + (data.length + 1) * rowHeight + 10;

  if (!sectionFits(doc, y, sectionHeight)) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(title, 20, y);
  drawLine(doc, y + 2);
  y += 8;

  const xConcepto = 17;
  const xValor = 17 + leftColWidth;

  doc.setFillColor(240, 240, 240);
  doc.rect(15, y - 4, leftColWidth + rightColWidth, rowHeight, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
  doc.text("CONCEPTO", xConcepto, y);
  doc.text("VALOR", xValor, y);
  y += rowHeight;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  for (const item of data) {
    doc.text(item.concepto, xConcepto, y);
    doc.text(item.valor, xValor, y);
    y += rowHeight;
  }

  return y + 5;
};

// Función para agregar sección de RECAUDOS (título + tabla de 3 columnas)
const addRecaudosSection = (doc, title, data, totalCantidad, totalValor, startY) => {
  let y = startY;
  const rowHeight = 6;
  const fontSize = 8;
  const colWidths = [65, 40, 55];
  const totalWidth = colWidths[0] + colWidths[1] + colWidths[2];

  const sectionHeight = 25 + (data.length + 3) * rowHeight + 15;

  if (!sectionFits(doc, y, sectionHeight)) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(title, 20, y);
  drawLine(doc, y + 2);
  y += 8;

  const xDescripcion = 17;
  const xCantidad = 17 + colWidths[0];
  const xTotal = 17 + colWidths[0] + colWidths[1];

  doc.setFillColor(240, 240, 240);
  doc.rect(15, y - 4, totalWidth, rowHeight, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
  doc.text("DESCRIPCION", xDescripcion, y);
  doc.text("CANTIDAD", xCantidad, y);
  doc.text("TOTAL", xTotal, y);
  y += rowHeight;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  for (const item of data) {
    doc.text(item.descripcion, xDescripcion, y);
    doc.text(item.cantidad, xCantidad, y);
    doc.text(item.total, xTotal, y);
    y += rowHeight;
  }

  drawLine(doc, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.text("TOTAL RECAUDO", xDescripcion, y);
  doc.text(totalCantidad, xCantidad, y);
  doc.setTextColor(46, 125, 50);
  doc.text(totalValor, xTotal, y);
  doc.setTextColor(0, 0, 0);

  return y + rowHeight + 10;
};

// Función para agregar sección de tabla de 4 columnas
const addFourColumnSection = (
  doc,
  sectionTitle,
  tableTitle,
  headers,
  data,
  totalLabel,
  totalCantidad,
  totalValor,
  startY
) => {
  let y = startY;
  const rowHeight = 5.5;
  const fontSize = 7.5;
  const colWidths = [20, 65, 32, 43];
  const totalWidth = colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3];

  const sectionHeight = 25 + (data.length + 3) * rowHeight + 15;

  if (!sectionFits(doc, y, sectionHeight)) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(sectionTitle, 20, y);
  drawLine(doc, y + 2);
  y += 8;

  const xCodigo = 17;
  const xDescripcion = 17 + colWidths[0];
  const xCantidad = 17 + colWidths[0] + colWidths[1];
  const xTotal = 17 + colWidths[0] + colWidths[1] + colWidths[2];

  doc.setFillColor(240, 240, 240);
  doc.rect(15, y - 4, totalWidth, rowHeight, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
  doc.text(headers[0], xCodigo, y);
  doc.text(headers[1], xDescripcion, y);
  doc.text(headers[2], xCantidad, y);
  doc.text(headers[3], xTotal, y);
  y += rowHeight;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  for (const row of data) {
    doc.text(row.codigo, xCodigo, y);
    doc.text(row.descripcion, xDescripcion, y);
    doc.text(row.cantidad, xCantidad, y);
    doc.text(row.total, xTotal, y);
    y += rowHeight;
  }

  drawLine(doc, y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(0, 0, 0);
  doc.text(totalLabel, xCodigo, y);
  doc.text(totalCantidad, xCantidad, y);
  doc.setTextColor(46, 125, 50);
  doc.text(totalValor, xTotal, y);
  doc.setTextColor(0, 0, 0);

  return y + rowHeight + 10;
};

// Función para agregar sección de CONTEO VEHICULAR detallado
const addConteoVehicularSection = (doc, title, data, startY) => {
  let y = startY;
  const rowHeight = 5.5;
  const fontSize = 7.5;
  const colWidths = [18, 55, 32, 45];
  const totalWidth = colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3];

  const sectionHeight = 25 + (data.length + 2) * rowHeight + 10;

  if (!sectionFits(doc, y, sectionHeight)) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(title, 20, y);
  drawLine(doc, y + 2);
  y += 8;

  const xCodigo = 17;
  const xDescripcion = 17 + colWidths[0];
  const xCantidad = 17 + colWidths[0] + colWidths[1];
  const xTotal = 17 + colWidths[0] + colWidths[1] + colWidths[2];

  doc.setFillColor(240, 240, 240);
  doc.rect(15, y - 4, totalWidth, rowHeight, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
  doc.text("CODIGO", xCodigo, y);
  doc.text("DESCRIPCION", xDescripcion, y);
  doc.text("CANTIDAD", xCantidad, y);
  doc.text("TOTAL", xTotal, y);
  y += rowHeight;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  for (const row of data) {
    doc.text(row.codigo, xCodigo, y);
    doc.text(row.descripcion, xDescripcion, y);
    doc.text(row.cantidad, xCantidad, y);
    doc.text(row.total, xTotal, y);
    y += rowHeight;
  }

  return y + 10;
};

// Función para agregar sección de DESCUENTO DE COMERCIOS (dinámica)
const addComerciosSection = (doc, title, data, totals, startY) => {
  let y = startY;
  const rowHeight = 4.5;
  const fontSize = 6;
  const colWidths = [12, 28, 16, 22, 16, 22, 16, 22, 16, 22];
  const totalWidth = colWidths.reduce((a, b) => a + b, 0);
  const marginLeft = 10;

  const sectionHeight = 30 + (data.length + 3) * rowHeight + 15;

  if (!sectionFits(doc, y, sectionHeight)) {
    doc.addPage();
    y = 20;
  }

  // Título de sección
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(title, 20, y);
  drawLine(doc, y + 2);
  y += 8;

  // Posiciones X
  const xCodigo = marginLeft;
  const xDescripcion = marginLeft + colWidths[0];
  const xCarroCant = marginLeft + colWidths[0] + colWidths[1];
  const xCarroTotal = marginLeft + colWidths[0] + colWidths[1] + colWidths[2];
  const xMotoCant = marginLeft + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3];
  const xMotoTotal =
    marginLeft + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4];
  const xBiciCant =
    marginLeft +
    colWidths[0] +
    colWidths[1] +
    colWidths[2] +
    colWidths[3] +
    colWidths[4] +
    colWidths[5];
  const xBiciTotal =
    marginLeft +
    colWidths[0] +
    colWidths[1] +
    colWidths[2] +
    colWidths[3] +
    colWidths[4] +
    colWidths[5] +
    colWidths[6];
  const xTotalCant =
    marginLeft +
    colWidths[0] +
    colWidths[1] +
    colWidths[2] +
    colWidths[3] +
    colWidths[4] +
    colWidths[5] +
    colWidths[6] +
    colWidths[7];
  const xTotalValor =
    marginLeft +
    colWidths[0] +
    colWidths[1] +
    colWidths[2] +
    colWidths[3] +
    colWidths[4] +
    colWidths[5] +
    colWidths[6] +
    colWidths[7] +
    colWidths[8];

  // Encabezados - Primera línea
  doc.setFillColor(240, 240, 240);
  doc.rect(marginLeft - 2, y - 4, totalWidth, rowHeight * 1.8, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);
  doc.text("COD", xCodigo, y);
  doc.text("DESCRIPCION", xDescripcion, y);
  doc.text("CARRO", xCarroCant, y);
  doc.text("CARRO", xCarroTotal, y);
  doc.text("MOTO", xMotoCant, y);
  doc.text("MOTO", xMotoTotal, y);
  doc.text("BICI", xBiciCant, y);
  doc.text("BICI", xBiciTotal, y);
  doc.text("TOTAL", xTotalCant, y);
  doc.text("TOTAL", xTotalValor, y);
  y += rowHeight;

  // Segunda línea de encabezados
  doc.setFont("helvetica", "bold");
  doc.setFontSize(fontSize - 0.5);
  doc.text("", xCodigo, y);
  doc.text("", xDescripcion, y);
  doc.text("(Cant)", xCarroCant + 4, y);
  doc.text("(Total)", xCarroTotal + 6, y);
  doc.text("(Cant)", xMotoCant + 4, y);
  doc.text("(Total)", xMotoTotal + 6, y);
  doc.text("(Cant)", xBiciCant + 4, y);
  doc.text("(Total)", xBiciTotal + 6, y);
  doc.text("(Cant)", xTotalCant + 6, y);
  doc.text("(Valor)", xTotalValor + 6, y);
  y += rowHeight - 1;

  // Línea separadora debajo de encabezados
  drawLine(doc, y - 2);
  y += 2;

  // Filas
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(0, 0, 0);

  for (const row of data) {
    doc.text(row.codigo, xCodigo, y);
    doc.text(row.descripcion, xDescripcion, y);
    doc.text(row.carroCantidad, xCarroCant + colWidths[2] - 3, y, { align: "right" });
    doc.text(row.carroTotal, xCarroTotal + colWidths[3] - 3, y, { align: "right" });
    doc.text(row.motoCantidad, xMotoCant + colWidths[4] - 3, y, { align: "right" });
    doc.text(row.motoTotal, xMotoTotal + colWidths[5] - 3, y, { align: "right" });
    doc.text(row.biciCantidad, xBiciCant + colWidths[6] - 3, y, { align: "right" });
    doc.text(row.biciTotal, xBiciTotal + colWidths[7] - 3, y, { align: "right" });
    doc.text(row.totalCantidad, xTotalCant + colWidths[8] - 3, y, { align: "right" });
    doc.text(row.totalValor, xTotalValor + colWidths[9] - 3, y, { align: "right" });
    y += rowHeight;
  }

  // Línea separadora antes de totales
  drawLine(doc, y);
  y += 4;

  // Totales
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(0, 0, 0);
  doc.text("TOTALES", xCodigo, y);
  doc.text(totals.totalCarroCantidad, xCarroCant + colWidths[2] - 3, y, { align: "right" });
  doc.text(totals.totalCarroValor, xCarroTotal + colWidths[3] - 3, y, { align: "right" });
  doc.text(totals.totalMotoCantidad, xMotoCant + colWidths[4] - 3, y, { align: "right" });
  doc.text(totals.totalMotoValor, xMotoTotal + colWidths[5] - 3, y, { align: "right" });
  doc.text(totals.totalBiciCantidad, xBiciCant + colWidths[6] - 3, y, { align: "right" });
  doc.text(totals.totalBiciValor, xBiciTotal + colWidths[7] - 3, y, { align: "right" });
  doc.text(totals.totalGeneralCantidad, xTotalCant + colWidths[8] - 3, y, { align: "right" });
  doc.setTextColor(46, 125, 50);
  doc.text(totals.totalGeneralValor, xTotalValor + colWidths[9] - 3, y, { align: "right" });
  doc.setTextColor(0, 0, 0);

  return y + rowHeight + 10;
};

// Función para procesar los datos de descuentosComercio
const procesarDescuentosComercio = (descuentos) => {
  if (!descuentos || descuentos.length === 0) {
    return [];
  }

  return descuentos.map((comercio, index) => {
    const nombreComercio = cleanComercioName(comercio.comercio);
    const totalCantidad =
      (comercio.countDescuentoCarroCommercio || 0) +
      (comercio.countDescuentoMotoCommercio || 0) +
      (comercio.countDescuentoBiciCommercio || 0);
    const totalValor =
      (comercio.descuentoCarroCommercio || 0) +
      (comercio.descuentoMotoCommercio || 0) +
      (comercio.descuentoBiciCommercio || 0);

    return {
      codigo: (index + 1).toString(),
      descripcion: nombreComercio,
      carroCantidad: formatNumber(comercio.countDescuentoCarroCommercio || 0),
      carroTotal: formatCurrency(comercio.descuentoCarroCommercio || 0),
      motoCantidad: formatNumber(comercio.countDescuentoMotoCommercio || 0),
      motoTotal: formatCurrency(comercio.descuentoMotoCommercio || 0),
      biciCantidad: formatNumber(comercio.countDescuentoBiciCommercio || 0),
      biciTotal: formatCurrency(comercio.descuentoBiciCommercio || 0),
      totalCantidad: formatNumber(totalCantidad),
      totalValor: formatCurrency(totalValor),
    };
  });
};

// Función para calcular totales de descuentos
const calcularTotalesDescuentos = (data) => {
  if (data.length === 0) {
    return {
      totalCarroCantidad: "0",
      totalCarroValor: formatCurrency(0),
      totalMotoCantidad: "0",
      totalMotoValor: formatCurrency(0),
      totalBiciCantidad: "0",
      totalBiciValor: formatCurrency(0),
      totalGeneralCantidad: "0",
      totalGeneralValor: formatCurrency(0),
    };
  }

  const totals = data.reduce(
    (acc, item) => {
      const carroCant = parseInt(item.carroCantidad.replace(/\./g, ""), 10) || 0;
      const carroVal = parseFloat(item.carroTotal.replace(/[^0-9]/g, "")) || 0;
      const motoCant = parseInt(item.motoCantidad.replace(/\./g, ""), 10) || 0;
      const motoVal = parseFloat(item.motoTotal.replace(/[^0-9]/g, "")) || 0;
      const biciCant = parseInt(item.biciCantidad.replace(/\./g, ""), 10) || 0;
      const biciVal = parseFloat(item.biciTotal.replace(/[^0-9]/g, "")) || 0;
      const totalCant = parseInt(item.totalCantidad.replace(/\./g, ""), 10) || 0;
      const totalVal = parseFloat(item.totalValor.replace(/[^0-9]/g, "")) || 0;

      return {
        totalCarroCantidad: acc.totalCarroCantidad + carroCant,
        totalCarroValor: acc.totalCarroValor + carroVal,
        totalMotoCantidad: acc.totalMotoCantidad + motoCant,
        totalMotoValor: acc.totalMotoValor + motoVal,
        totalBiciCantidad: acc.totalBiciCantidad + biciCant,
        totalBiciValor: acc.totalBiciValor + biciVal,
        totalGeneralCantidad: acc.totalGeneralCantidad + totalCant,
        totalGeneralValor: acc.totalGeneralValor + totalVal,
      };
    },
    {
      totalCarroCantidad: 0,
      totalCarroValor: 0,
      totalMotoCantidad: 0,
      totalMotoValor: 0,
      totalBiciCantidad: 0,
      totalBiciValor: 0,
      totalGeneralCantidad: 0,
      totalGeneralValor: 0,
    }
  );

  return {
    totalCarroCantidad: formatNumber(totals.totalCarroCantidad),
    totalCarroValor: formatCurrency(totals.totalCarroValor),
    totalMotoCantidad: formatNumber(totals.totalMotoCantidad),
    totalMotoValor: formatCurrency(totals.totalMotoValor),
    totalBiciCantidad: formatNumber(totals.totalBiciCantidad),
    totalBiciValor: formatCurrency(totals.totalBiciValor),
    totalGeneralCantidad: formatNumber(totals.totalGeneralCantidad),
    totalGeneralValor: formatCurrency(totals.totalGeneralValor),
  };
};

// ============================================
// FUNCIÓN PRINCIPAL
// ============================================
export const generarPDFInforme = async (data) => {
  try {
    const { formData, reportData, empresaData } = data;

    if (!reportData) {
      throw new Error("No hay datos del informe para exportar");
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // ============================================
    // ENCABEZADO
    // ============================================
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(empresaData.nombre || "PAGOS AUTOMATICOS DE COLOMBIA S.A.S", pageWidth / 2, y, {
      align: "center",
    });
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(empresaData.servicio || "CONTROLES ACCESO VEHICULAR", pageWidth / 2, y, {
      align: "center",
    });
    y += 6;

    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(empresaData.version || "LINCE V7.0.R3", pageWidth / 2, y, { align: "center" });
    y += 8;

    drawLine(doc, y);
    y += 6;

    // LICENCIADO A
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("LICENCIADO A:", 20, y);
    y += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(0, 0, 0);

    doc.setFont("helvetica", "bold");
    doc.text("RAZON SOCIAL", 25, y);
    doc.setFont("helvetica", "normal");
    doc.text(empresaData.razonSocial || "CENTRO COMERCIAL ISERRA 100 PH", 65, y);
    y += 6;

    doc.setFont("helvetica", "bold");
    doc.text("NIT", 25, y);
    doc.setFont("helvetica", "normal");
    doc.text(empresaData.nit || "830.019.348-2", 65, y);
    y += 6;

    doc.setFont("helvetica", "bold");
    doc.text("#ID MAQUINA", 25, y);
    doc.setFont("helvetica", "normal");
    doc.text(empresaData.idMaquina || "ISERRA 100", 65, y);
    y += 12;

    drawLine(doc, y);
    y += 8;

    // Título del reporte
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `REPORTE DIARIO - ${
        formData.fechaFormateada || formatSimpleDate(formData.date) || "Fecha no disponible"
      }`,
      pageWidth / 2,
      y,
      { align: "center" }
    );
    y += 10;

    drawLine(doc, y);
    y += 8;

    // Información de filtros
    const fechaGeneracion = formatDateTime(new Date().toISOString());
    doc.setFont("helvetica", "bold");
    doc.text("Fecha Generación:", 20, y);
    doc.setFont("helvetica", "normal");
    doc.text(fechaGeneracion, 65, y);

    y += 10;
    drawLine(doc, y);
    y += 12;

    // ============================================
    // CIERRE DE CAJA
    // ============================================
    // Usar la función getCajaDisplayValue para obtener el valor correcto de caja
    const cajaDisplayValue = getCajaDisplayValue(formData.box, reportData);

    const cierreData = [
      { concepto: "USUARIO", valor: formData.user_kme !== "TODOS" ? formData.user_kme : "Todos" },
      { concepto: "CAJA", valor: cajaDisplayValue },
      { concepto: "FECHA", valor: formatSimpleDate(formData.date) },
      { concepto: "PRIMER LOGIN", valor: formatCashClosingTime(reportData.login?.primerLogin) },
      { concepto: "ÚLTIMO CIERRE", valor: formatCashClosingTime(reportData.login?.ultimoCierre) },
      { concepto: "PRIMERA FACTURA", valor: reportData.facturacion?.primeraFactura || "N/A" },
      { concepto: "ÚLTIMA FACTURA", valor: reportData.facturacion?.ultimaFactura || "N/A" },
    ];

    y = addTwoColumnSection(doc, "CIERRE DE CAJA", cierreData, y, {
      leftColWidth: 45,
      rightColWidth: 125,
    });
    y += 5;

    // ============================================
    // MOVIMIENTO DIARIO POR TARIFA
    // ============================================
    const movimientoData = [
      { concepto: "EFECTIVO INICIAL", valor: formatCurrency(0) },
      { concepto: "RECAUDO TARIFAS", valor: formatCurrency(reportData.recaudos?.tarifas || 0) },
      { concepto: "REC. MENSUALES", valor: formatCurrency(reportData.recaudos?.mensuales || 0) },
      { concepto: "REC. MISCELANEOS", valor: formatCurrency(reportData.recaudos?.miselaneos || 0) },
      { concepto: "RETIROS", valor: formatCurrency(reportData.recaudos?.retiros || 0) },
      { concepto: "REC. PERDIDOS", valor: formatCurrency(reportData.recaudos?.perdidos || 0) },
      { concepto: "BALANCE DE CAJA", valor: formatCurrency(reportData.recaudos?.balanceCaja || 0) },
    ];

    y = addTwoColumnSection(doc, "MOVIMIENTO DIARIO POR TARIFA", movimientoData, y, {
      leftColWidth: 50,
      rightColWidth: 120,
    });
    y += 5;

    // ============================================
    // RECAUDOS
    // ============================================
    const recaudosItems = [
      {
        descripcion: "TARIFAS",
        cantidad: formatNumber(reportData.recaudosTable?.tarifas?.cantidad || 0),
        total: formatCurrency(reportData.recaudosTable?.tarifas?.total || 0),
      },
      {
        descripcion: "PERDIDOS",
        cantidad: formatNumber(reportData.recaudosTable?.perdidos?.cantidad || 0),
        total: formatCurrency(reportData.recaudosTable?.perdidos?.total || 0),
      },
      { descripcion: "CONTINGENCIAS", cantidad: formatNumber(0), total: formatCurrency(0) },
      {
        descripcion: "MENSUALES",
        cantidad: formatNumber(reportData.recaudosTable?.mensuales?.cantidad || 0),
        total: formatCurrency(reportData.recaudosTable?.mensuales?.total || 0),
      },
      { descripcion: "RECARGAS PREPAGOS", cantidad: formatNumber(0), total: formatCurrency(0) },
      {
        descripcion: "MISCELANEOS",
        cantidad: formatNumber(reportData.recaudosTable?.miscelaneos?.cantidad || 0),
        total: formatCurrency(reportData.recaudosTable?.miscelaneos?.total || 0),
      },
      {
        descripcion: "IMPUESTOS",
        cantidad: formatNumber(reportData.recaudosTable?.impuestos?.cantidad || 0),
        total: formatCurrency(reportData.recaudosTable?.impuestos?.total || 0),
      },
    ];

    const totalCantidad = formatNumber(
      (reportData.recaudosTable?.tarifas?.cantidad || 0) +
        (reportData.recaudosTable?.perdidos?.cantidad || 0) +
        (reportData.recaudosTable?.mensuales?.cantidad || 0) +
        (reportData.recaudosTable?.miscelaneos?.cantidad || 0)
    );

    const totalValor = formatCurrency(
      (reportData.recaudosTable?.tarifas?.total || 0) +
        (reportData.recaudosTable?.perdidos?.total || 0) +
        (reportData.recaudosTable?.mensuales?.total || 0) +
        (reportData.recaudosTable?.miscelaneos?.total || 0) +
        (reportData.recaudosTable?.impuestos?.total || 0)
    );

    y = addRecaudosSection(doc, "RECAUDOS", recaudosItems, totalCantidad, totalValor, y);
    y += 5;

    // ============================================
    // CONTADORES DE CAJERO
    // ============================================
    const contadoresData = [
      { concepto: "TRANSACCIONES", valor: formatNumber(reportData.contadores?.transacciones || 0) },
      { concepto: "CANCELACIONES", valor: formatNumber(reportData.contadores?.cancelaciones || 0) },
      {
        concepto: "INICIO DE SISTEMA",
        valor: formatNumber(reportData.contadores?.inicioSistema || 0),
      },
      {
        concepto: "APERTURA BARRERA ENTRADA",
        valor: formatNumber(reportData.contadores?.aperturaBarreraEntrada || 0),
      },
      {
        concepto: "APERTURA BARRERA SALIDA",
        valor: formatNumber(reportData.contadores?.aperturaBarreraSalida || 0),
      },
      {
        concepto: "SALIDA EN CONTINGENCIA",
        valor: formatNumber(reportData.contadores?.salidaContingencia || 0),
      },
      {
        concepto: "TIQUETES PURGADOS",
        valor: formatNumber(reportData.contadores?.tiquetesPurgados || 0),
      },
    ];

    y = addTwoColumnSection(doc, "CONTADORES DE CAJERO", contadoresData, y, {
      leftColWidth: 55,
      rightColWidth: 115,
    });
    y += 5;

    // ============================================
    // CONTEO VEHICULAR
    // ============================================
    const facturasRaw = reportData?.raw?.facturasEmiAnu || [];
    const facturaAnulada = facturasRaw.find((item) => item.status_descripcion === "ANULADA");
    const facturasAnuladas = facturaAnulada ? parseInt(facturaAnulada.total, 10) : 0;
    const vehiculosConTiquetes = reportData?.conteoResumen?.vehiculosConTiquetes || 0;
    const tiquetesPerdidos = reportData?.conteoResumen?.tiquetesPerdidos || 0;
    const salidaEnGracia = 0;
    const facturasEmitidasCalculadas =
      vehiculosConTiquetes - tiquetesPerdidos - facturasAnuladas - salidaEnGracia;
    const sinFacturaPorDescuento = reportData?.conteoResumen?.sinFacturaPorDescuento || 0;

    const conteoResumenData = [
      { concepto: "VEHÍCULOS CON TIQUETES", valor: formatNumber(vehiculosConTiquetes) },
      { concepto: "TIQUETES PERDIDOS", valor: formatNumber(tiquetesPerdidos) },
      { concepto: "SALIDA EN GRACIA", valor: formatNumber(salidaEnGracia) },
      { concepto: "FACTURAS EMITIDAS", valor: formatNumber(facturasEmitidasCalculadas) },
      { concepto: "FACTURAS ANULADAS", valor: formatNumber(facturasAnuladas) },
      { concepto: "SIN FACTURA POR DESCUENTO", valor: formatNumber(sinFacturaPorDescuento) },
    ];

    y = addTwoColumnSection(doc, "CONTEO VEHICULAR", conteoResumenData, y, {
      leftColWidth: 60,
      rightColWidth: 110,
    });
    y += 5;

    // ============================================
    // CONTEO VEHICULAR DETALLADO
    // ============================================
    const conteoTableData = (reportData.conteoVehicularTable || []).map((item) => ({
      codigo: item.codigo,
      descripcion: item.descripcion,
      cantidad: formatNumber(item.cantidad),
      total: formatCurrency(item.total),
    }));

    y = addConteoVehicularSection(doc, "CONTEO VEHICULAR (DETALLADO)", conteoTableData, y);
    y += 5;

    // ============================================
    // RECAUDO POR PERDIDOS
    // ============================================
    const perdidosCantidad = reportData.movimientos?.countTicketPerdidos || 0;
    const perdidosTotal = reportData.movimientos?.ticketesPerdidos || 0;

    const perdidosData = [
      {
        codigo: "VEHI",
        descripcion: "Vehículos",
        cantidad: formatNumber(perdidosCantidad),
        total: formatCurrency(perdidosTotal),
      },
    ];

    y = addFourColumnSection(
      doc,
      "RECAUDO POR PERDIDOS",
      "RECAUDO POR PERDIDOS",
      ["CODIGO", "DESCRIPCION", "CANTIDAD", "TOTAL"],
      perdidosData,
      "TOTAL PERDIDOS",
      formatNumber(perdidosCantidad),
      formatCurrency(perdidosTotal),
      y
    );
    y += 5;

    // ============================================
    // RECAUDO POR MISCELANEOS
    // ============================================
    const miscelaneosCantidad = reportData.movimientos?.miselaneos || 0;
    const miscelaneosTotal = reportData.movimientos?.totalMiselaneos || 0;

    const miscelaneosData = [
      {
        codigo: "01",
        descripcion: "Misceláneos",
        cantidad: formatNumber(miscelaneosCantidad),
        total: formatCurrency(miscelaneosTotal),
      },
    ];

    y = addFourColumnSection(
      doc,
      "RECAUDO POR MISCELANEOS",
      "RECAUDO POR MISCELANEOS",
      ["CODIGO", "DESCRIPCION", "CANTIDAD", "TOTAL"],
      miscelaneosData,
      "TOTAL MISCELANEOS",
      formatNumber(miscelaneosCantidad),
      formatCurrency(miscelaneosTotal),
      y
    );
    y += 5;

    // ============================================
    // RECAUDO POR RECARGAS DE PREPAGO
    // ============================================
    const prepagoData = [
      {
        codigo: "02",
        descripcion: "REC. PREPAGOS",
        cantidad: formatNumber(0),
        total: formatCurrency(0),
      },
    ];

    y = addFourColumnSection(
      doc,
      "RECAUDO POR RECARGAS DE PREPAGO",
      "RECAUDO POR RECARGAS DE PREPAGO",
      ["CODIGO", "DESCRIPCION", "CANTIDAD", "TOTAL"],
      prepagoData,
      "TOTAL RECARGAS PREPAGO",
      formatNumber(0),
      formatCurrency(0),
      y
    );
    y += 5;

    // ============================================
    // RECAUDO POR IMPUESTOS
    // ============================================
    const impuestosCantidad = reportData.recaudosTable?.impuestos?.cantidad || 0;
    const impuestosTotal = reportData.recaudosTable?.impuestos?.total || 0;

    const impuestosData = [
      {
        codigo: "103",
        descripcion: "IVA 19%",
        cantidad: formatNumber(impuestosCantidad),
        total: formatCurrency(impuestosTotal),
      },
    ];

    y = addFourColumnSection(
      doc,
      "RECAUDO POR IMPUESTOS",
      "RECAUDO POR IMPUESTOS",
      ["CODIGO", "DESCRIPCION", "CANTIDAD", "TOTAL"],
      impuestosData,
      "TOTAL IMPUESTOS",
      formatNumber(impuestosCantidad),
      formatCurrency(impuestosTotal),
      y
    );
    y += 5;

    // ============================================
    // DESCUENTO DE COMERCIOS (DINÁMICO)
    // ============================================
    const descuentosRaw = reportData?.movimientos?.descuentosComercio || [];
    const comerciosData = procesarDescuentosComercio(descuentosRaw);
    const comerciosTotales = calcularTotalesDescuentos(comerciosData);

    if (comerciosData.length > 0) {
      y = addComerciosSection(doc, "DESCUENTO DE COMERCIOS", comerciosData, comerciosTotales, y);
      y += 5;
    } else {
      // Si no hay datos, mostrar mensaje
      y = addTwoColumnSection(
        doc,
        "DESCUENTO DE COMERCIOS",
        [{ concepto: "No hay datos", valor: "" }],
        y,
        {
          leftColWidth: 80,
          rightColWidth: 90,
        }
      );
      y += 5;
    }

    // ============================================
    // PAGOS CON PREPAGO
    // ============================================
    const pagosPrepagoData = [
      { descripcion: "Vehículos", cantidad: formatNumber(0), total: formatCurrency(0) },
    ];

    y = addRecaudosSection(
      doc,
      "PAGOS CON PREPAGO",
      pagosPrepagoData,
      formatNumber(0),
      formatCurrency(0),
      y
    );
    y += 5;

    // ============================================
    // FORMAS DE PAGO
    // ============================================
    // Obtener datos específicos de formas de pago
    const formasPagoEfectivoTotal = reportData?.totals?.formasPago?.efectivo?.total || 0;
    const formasPagoEfectivoCount = reportData?.totals?.formasPago?.efectivo?.count || 0;
    const formasPagoTarjetaTotal = reportData?.totals?.formasPago?.tarjeta?.total || 0;
    const formasPagoTarjetaCount = reportData?.totals?.formasPago?.tarjeta?.count || 0;

    const formasPagoItems = [
      {
        descripcion: "EFECTIVO",
        cantidad: formatNumber(formasPagoEfectivoCount),
        total: formatCurrency(formasPagoEfectivoTotal),
      },
      {
        descripcion: "TARJETA",
        cantidad: formatNumber(formasPagoTarjetaCount),
        total: formatCurrency(formasPagoTarjetaTotal),
      },
      { descripcion: "PREPAGO", cantidad: formatNumber(0), total: formatCurrency(0) },
    ];

    const totalFormasCantidad = formatNumber(formasPagoEfectivoCount + formasPagoTarjetaCount);
    const totalFormasValor = formatCurrency(formasPagoEfectivoTotal + formasPagoTarjetaTotal);

    y = addRecaudosSection(
      doc,
      "FORMAS DE PAGO",
      formasPagoItems,
      totalFormasCantidad,
      totalFormasValor,
      y
    );
    y += 10;

    // ============================================
    // PIE DE PÁGINA
    // ============================================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(doc, empresaData, i, totalPages);
    }

    // Descargar PDF
    doc.save(`informe_diario_${formData.date || "fecha"}_${Date.now()}.pdf`);

    return {
      success: true,
      fileName: `informe_diario_${formData.date || "fecha"}_${Date.now()}.pdf`,
      message: "PDF generado exitosamente",
    };
  } catch (error) {
    console.error("❌ Error generando PDF:", error);

    const errorDoc = new jsPDF();
    errorDoc.setFont("helvetica", "bold");
    errorDoc.setFontSize(14);
    errorDoc.setTextColor(0, 0, 0);
    errorDoc.text("Error al generar informe", 20, 30);
    errorDoc.setFontSize(10);
    errorDoc.setTextColor(0, 0, 0);
    errorDoc.text("No se pudo generar el informe solicitado.", 20, 50);
    errorDoc.text("Por favor, contacte al administrador del sistema.", 20, 60);
    errorDoc.text(`Error: ${error.message}`, 30, 80);

    errorDoc.save(`error_informe_${Date.now()}.pdf`);

    throw new Error(`Error generando PDF: ${error.message}`);
  }
};
